import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CollectionIcon, PlusIcon } from "@heroicons/react/outline";

import ApiService from "../services/ApiService";
import EasyTrans from "../components/bundel/EasyTrans";
import Spinner from "../components/bundel/Spinner";
import WorkGridCard from "../components/bundel/WorkGridCard";
import WorkGridSkeleton from "../components/bundel/WorkGridSkeleton";
import { Button, EmptyState } from "../components/ui";

const ITEMS_PER_PAGE = 20;

const MyWorksPage = () => {
  const [works, setWorks] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const sentinelRef = useRef(null);

  const fetchPage = useCallback(async (pageToLoad, { append }) => {
    append ? setIsLoadingMore(true) : setIsLoading(true);
    try {
      const result = await ApiService.getUserWorkList(pageToLoad, ITEMS_PER_PAGE);
      const content = result?.content ?? [];
      setTotalPages(result?.totalPages ?? 0);
      setWorks((prev) => (append ? [...prev, ...content] : content));
    } catch (error) {
      console.error("Error:", error);
    } finally {
      append ? setIsLoadingMore(false) : setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(0, { append: false });
  }, [fetchPage]);

  useEffect(() => {
    if (page > 0) fetchPage(page, { append: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const hasMore = page < totalPages - 1;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoading &&
          !isLoadingMore
        ) {
          setPage((p) => p + 1);
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore]);

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 sm:my-6 lg:my-10">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          <EasyTrans>Navigation.MyWorks</EasyTrans>
          {!isLoading && works.length > 0 && (
            <span className="ml-2 text-base font-normal text-gray-400">
              {works.length}
            </span>
          )}
        </h1>
        <Link to="/registerwork">
          <Button variant="primary" size="sm">
            <PlusIcon className="h-4 w-4 mr-1.5" aria-hidden="true" />
            <EasyTrans>Navigation.Workregister</EasyTrans>
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <WorkGridSkeleton />
      ) : works.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {works.map((work, index) => (
              <WorkGridCard key={work.id ?? index} work={work} owned />
            ))}
          </div>
          <div ref={sentinelRef} className="h-10" />
          {isLoadingMore && (
            <div className="flex justify-center pb-6">
              <Spinner />
            </div>
          )}
        </>
      ) : (
        <EmptyState
          icon={CollectionIcon}
          title={<EasyTrans>NoElementsFound</EasyTrans>}
          hint={<EasyTrans>NoOwnWorksHint</EasyTrans>}
          action={
            <Link to="/registerwork">
              <Button variant="primary">
                <PlusIcon className="h-4 w-4 mr-1.5" aria-hidden="true" />
                <EasyTrans>Navigation.Workregister</EasyTrans>
              </Button>
            </Link>
          }
        />
      )}
    </div>
  );
};

export default MyWorksPage;
